import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shield, ArrowSquareOut, Info, PlugsConnected, MagnifyingGlass, UsersThree, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SSO() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ldapTesting, setLdapTesting] = useState(false);
  const [ldapTestResult, setLdapTestResult] = useState(null);
  const [ldapSyncing, setLdapSyncing] = useState(false);

  const load = () => api.get("/sso/config").then(({ data }) => setConfig(data));
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.patch("/sso/config", patch);
      toast.success("SSO config saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const ldapTest = async () => {
    setLdapTesting(true);
    setLdapTestResult(null);
    try {
      const { data } = await api.post("/sso/ldap/test", { config: config.ldap });
      setLdapTestResult(data);
      if (data.ok) toast.success(data.message || "Bind successful");
      else toast.error(data.message || "Bind failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setLdapTesting(false); }
  };

  const ldapSync = async () => {
    if (!confirm("Import users from LDAP into this tenant?")) return;
    setLdapSyncing(true);
    try {
      const { data } = await api.post("/sso/ldap/sync-users", { config: config.ldap, limit: 200 });
      if (data.ok) toast.success(`Imported ${data.imported} users (${data.skipped} already existed)`);
      else toast.error(data.message || "Sync failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setLdapSyncing(false); }
  };

  const fillForumsysDemo = () => {
    setConfig({
      ...config,
      ldap: {
        host: "ldap.forumsys.com",
        port: 389,
        use_ssl: false,
        bind_dn: "cn=read-only-admin,dc=example,dc=com",
        bind_password: "password",
        base_dn: "dc=example,dc=com",
        user_filter: "(uid={username})",
        attr_email: "mail",
        attr_name: "cn",
      },
    });
    toast.info("Public demo config loaded — click 'Test bind' to verify");
  };

  if (!config) return <PageShell title="SSO"><div className="h-64 glass animate-pulse" /></PageShell>;

  return (
    <PageShell
      title="SSO & Directory"
      subtitle="Corporate single sign-on and directory integration"
      right={<span className="chip chip-neutral">Enterprise feature</span>}
    >
      <GlassCard strong className="p-5 mb-6 flex items-start gap-3">
        <Info size={20} weight="duotone" className="text-[var(--accent)] shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium">Enable SSO to map corporate users to Clariva automatically.</div>
          <div className="text-[var(--text-2)] mt-1">Supported: SAML 2.0 (Okta, Azure AD, OneLogin), OIDC, Active Directory / LDAP, Google Workspace domain login. Enterprise tier required for production use.</div>
        </div>
      </GlassCard>

      <GlassCard className="p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Status</div>
            <div className="text-lg font-medium mt-1" style={{ fontFamily: 'var(--font-heading)' }}>SSO is currently {config.enabled ? "enabled" : "disabled"}</div>
            <div className="text-xs text-[var(--text-3)] mt-1">Provider: {config.provider || "—"}</div>
          </div>
          <Switch checked={config.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="sso-enabled-toggle" />
        </div>
      </GlassCard>

      <Tabs defaultValue="saml" className="space-y-6">
        <TabsList className="glass-subtle p-1">
          <TabsTrigger value="saml" data-testid="sso-tab-saml">SAML 2.0</TabsTrigger>
          <TabsTrigger value="oidc" data-testid="sso-tab-oidc">OIDC</TabsTrigger>
          <TabsTrigger value="ldap" data-testid="sso-tab-ldap">AD / LDAP</TabsTrigger>
          <TabsTrigger value="google" data-testid="sso-tab-google">Google Workspace</TabsTrigger>
        </TabsList>

        <TabsContent value="saml">
          <GlassCard className="p-6 space-y-4">
            <div>
              <Label className="text-xs text-[var(--text-2)]">Identity Provider URL (IdP metadata)</Label>
              <Input value={config.saml?.idp_url || ""} onChange={(e) => setConfig({ ...config, saml: { ...config.saml, idp_url: e.target.value } })} placeholder="https://company.okta.com/app/clariva/sso/saml" data-testid="saml-idp-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Entity ID</Label>
              <Input value={config.saml?.entity_id || ""} onChange={(e) => setConfig({ ...config, saml: { ...config.saml, entity_id: e.target.value } })} placeholder="urn:clariva:sp" data-testid="saml-entity-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">X.509 Certificate (PEM)</Label>
              <textarea value={config.saml?.cert || ""} onChange={(e) => setConfig({ ...config, saml: { ...config.saml, cert: e.target.value } })} placeholder="-----BEGIN CERTIFICATE-----&#10;..." data-testid="saml-cert-input" className="mt-1.5 w-full glass-subtle border border-[var(--border)] rounded-md p-3 font-mono text-xs" rows={6} />
            </div>
            <div className="p-3 rounded-md bg-[var(--surface)] border border-[var(--border)] text-xs font-mono">
              <div className="text-[var(--text-3)] mb-1">ACS URL (use this in your IdP):</div>
              <div>https://app.clariva.ai/sso/saml/acs/{config.tenant_id?.slice(0, 8)}</div>
            </div>
            <Button onClick={() => save({ provider: "saml", saml: config.saml })} disabled={saving} data-testid="sso-saml-save" className="gap-2"><Shield size={14} /> Save SAML config</Button>
          </GlassCard>
        </TabsContent>

        <TabsContent value="oidc">
          <GlassCard className="p-6 space-y-4">
            <div>
              <Label className="text-xs text-[var(--text-2)]">Issuer URL</Label>
              <Input value={config.oidc?.issuer || ""} onChange={(e) => setConfig({ ...config, oidc: { ...config.oidc, issuer: e.target.value } })} placeholder="https://login.microsoftonline.com/TENANT" data-testid="oidc-issuer-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Client ID</Label>
              <Input value={config.oidc?.client_id || ""} onChange={(e) => setConfig({ ...config, oidc: { ...config.oidc, client_id: e.target.value } })} data-testid="oidc-clientid-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Client Secret</Label>
              <Input type="password" placeholder={config.oidc?.client_secret_set ? "••••••• (set — re-enter to change)" : ""} data-testid="oidc-secret-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <Button onClick={() => save({ provider: "oidc", oidc: config.oidc })} disabled={saving} data-testid="sso-oidc-save" className="gap-2"><Shield size={14} /> Save OIDC config</Button>
          </GlassCard>
        </TabsContent>

        <TabsContent value="ldap">
          <GlassCard className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm font-semibold">Active Directory / LDAP bind</div>
                <div className="text-xs text-[var(--text-3)] mt-1">Real-time bind against any LDAPv3 server. Test before saving.</div>
              </div>
              <Button variant="outline" size="sm" onClick={fillForumsysDemo} data-testid="ldap-fill-demo-btn" className="gap-2 text-xs">
                <PlugsConnected size={12} /> Load public demo (forumsys)
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[var(--text-2)]">Host</Label>
                <Input value={config.ldap?.host || ""} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, host: e.target.value } })} placeholder="directory.company.local" data-testid="ldap-host-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-2)]">Port</Label>
                <Input type="number" value={config.ldap?.port || 389} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, port: Number(e.target.value) } })} data-testid="ldap-port-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={!!config.ldap?.use_ssl}
                onCheckedChange={(v) => setConfig({ ...config, ldap: { ...config.ldap, use_ssl: v, port: v ? 636 : 389 } })}
                data-testid="ldap-ssl-switch"
              />
              <Label className="text-xs text-[var(--text-2)]">Use LDAPS (SSL) — port 636</Label>
            </div>

            <div>
              <Label className="text-xs text-[var(--text-2)]">Bind DN (service account)</Label>
              <Input value={config.ldap?.bind_dn || ""} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, bind_dn: e.target.value } })} placeholder="cn=clariva-svc,ou=service-accounts,dc=company,dc=local" data-testid="ldap-binddn-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Bind password</Label>
              <Input type="password" value={config.ldap?.bind_password || ""} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, bind_password: e.target.value } })} placeholder="••••••••" data-testid="ldap-bindpw-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-2)]">Base DN</Label>
              <Input value={config.ldap?.base_dn || ""} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, base_dn: e.target.value } })} placeholder="dc=company,dc=local" data-testid="ldap-basedn-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[var(--text-2)]">User filter</Label>
                <Input value={config.ldap?.user_filter || "(uid={username})"} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, user_filter: e.target.value } })} placeholder="(uid={username})" data-testid="ldap-userfilter-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-2)]">Search filter</Label>
                <Input value={config.ldap?.search_filter || "(objectClass=inetOrgPerson)"} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, search_filter: e.target.value } })} data-testid="ldap-searchfilter-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-[var(--text-2)]">Email attribute</Label>
                <Input value={config.ldap?.attr_email || "mail"} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, attr_email: e.target.value } })} data-testid="ldap-attremail-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-2)]">Name attribute</Label>
                <Input value={config.ldap?.attr_name || "cn"} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, attr_name: e.target.value } })} data-testid="ldap-attrname-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
              <div>
                <Label className="text-xs text-[var(--text-2)]">Role attribute</Label>
                <Input value={config.ldap?.attr_role || "title"} onChange={(e) => setConfig({ ...config, ldap: { ...config.ldap, attr_role: e.target.value } })} data-testid="ldap-attrrole-input" className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs" />
              </div>
            </div>

            {ldapTestResult && (
              <div
                data-testid="ldap-test-result"
                className={`p-3 rounded-md border text-xs flex items-start gap-2 ${
                  ldapTestResult.ok
                    ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
                    : "border-red-500/30 bg-red-500/5 text-red-400"
                }`}
              >
                {ldapTestResult.ok ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
                <div className="flex-1">
                  <div className="font-medium">{ldapTestResult.message}</div>
                  {ldapTestResult.details?.naming_contexts?.length ? (
                    <div className="mt-1 opacity-80">Naming contexts: {ldapTestResult.details.naming_contexts.join(", ")}</div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={() => save({ provider: "ldap", ldap: config.ldap })} disabled={saving} data-testid="sso-ldap-save" className="gap-2">
                <Shield size={14} /> Save
              </Button>
              <Button variant="outline" onClick={ldapTest} disabled={ldapTesting} data-testid="sso-ldap-test" className="gap-2">
                <PlugsConnected size={14} /> {ldapTesting ? "Testing…" : "Test bind"}
              </Button>
              <Button variant="outline" onClick={ldapSync} disabled={ldapSyncing} data-testid="sso-ldap-sync" className="gap-2">
                <UsersThree size={14} /> {ldapSyncing ? "Syncing…" : "Sync users"}
              </Button>
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="google">
          <GlassCard className="p-6 space-y-4">
            <div>
              <Label className="text-xs text-[var(--text-2)]">Allowed Google Workspace domains</Label>
              <Input
                value={(config.google_domains || []).join(", ")}
                onChange={(e) => setConfig({ ...config, google_domains: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="company.com, subsidiary.co"
                data-testid="google-domains-input"
                className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs"
              />
              <div className="text-xs text-[var(--text-3)] mt-2">Only users with emails on these domains can sign in with Google.</div>
            </div>
            <Button onClick={() => save({ provider: "google", google_domains: config.google_domains })} disabled={saving} data-testid="sso-google-save" className="gap-2"><Shield size={14} /> Save Google SSO</Button>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
